import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Copy, Check, Edit3, ExternalLink, TrendingUp, MousePointerClick, ShoppingBag, DollarSign } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { fmt } from "../lib/money";

/**
 * Standalone affiliate dashboard — works WITHOUT Trackmatrix.
 * Every authenticated user automatically gets a unique slug they can share.
 * Click + conversion + commission tracking is all native to guyanadiscounts.com.
 */
export default function Affiliate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedHome, setCopiedHome] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setData(data);
      setNewSlug(data.slug);
    } catch (e) {
      toast.error("Couldn't load your affiliate dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSlug = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.put("/affiliate/me/slug", { slug: newSlug.trim().toLowerCase() });
      toast.success("Slug updated.");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update slug.");
    } finally {
      setSaving(false);
    }
  };

  const copyHome = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.share_home_url);
      setCopiedHome(true);
      setTimeout(() => setCopiedHome(false), 2000);
      toast.success("Share link copied to clipboard.");
    } catch {
      toast.error("Could not copy. Long-press the link to copy manually.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-sm text-neutral-500">Loading your affiliate dashboard…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-sm text-[#FF2A00]">Couldn't load. Try again later.</div>
      </div>
    );
  }

  const s = data.stats;

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
      {/* Header */}
      <div className="mb-8 flex items-start gap-3">
        <div className="w-10 h-10 bg-[#0033FF] text-white flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight">Affiliate dashboard</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Share your link, earn commission on every order you drive. Paid out monthly.
          </p>
        </div>
      </div>

      {/* Slug + share box */}
      <div className="border-2 border-black bg-[#F0F4FF] p-5 sm:p-6 mb-8">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#0033FF] mb-1">Your affiliate code</div>
        {editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              data-testid="affiliate-slug-input"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              maxLength={30}
              className="h-10 px-3 border-2 border-black font-mono text-base font-bold flex-1 min-w-[200px]"
              placeholder="your-name-here"
            />
            <button
              data-testid="affiliate-slug-save"
              onClick={saveSlug}
              disabled={saving}
              className="h-10 px-4 bg-[#0033FF] text-white text-sm font-semibold disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => { setEditing(false); setNewSlug(data.slug); }}
              className="h-10 px-3 border border-black text-sm font-semibold"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <code
              data-testid="affiliate-slug"
              className="font-mono text-2xl sm:text-3xl font-black break-all"
            >
              {data.slug}
            </code>
            <button
              data-testid="affiliate-slug-edit"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 text-xs text-[#0033FF] hover:underline"
            >
              <Edit3 className="w-3 h-3" /> change
            </button>
          </div>
        )}

        <div className="mt-5 pt-5 border-t border-[#0033FF]/30">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#0033FF] mb-1">
            Your homepage share link
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code
              data-testid="affiliate-home-url"
              className="font-mono text-xs sm:text-sm bg-white px-2 py-1.5 border border-black flex-1 min-w-[200px] break-all"
            >
              {data.share_home_url}
            </code>
            <button
              data-testid="affiliate-copy-home"
              onClick={copyHome}
              className="h-9 inline-flex items-center gap-1 px-3 bg-black hover:bg-[#0033FF] text-white text-xs font-semibold"
            >
              {copiedHome ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedHome ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-[11px] text-neutral-600 mt-2 leading-snug">
            Anyone who clicks this and places an order will credit you with commission — works in WhatsApp, SMS, Facebook, anywhere. Use the <Link to="/products" className="underline">products page</Link> to share specific items.
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard
          testid="stat-clicks"
          icon={<MousePointerClick className="w-4 h-4" />}
          label="Clicks (all time)"
          value={s.clicks_total.toLocaleString()}
          accent={`${s.clicks_30d.toLocaleString()} this month`}
        />
        <StatCard
          testid="stat-conversions"
          icon={<ShoppingBag className="w-4 h-4" />}
          label="Conversions"
          value={s.conversions.toLocaleString()}
          accent={s.clicks_total > 0 ? `${s.conversion_rate_pct}% rate` : "—"}
        />
        <StatCard
          testid="stat-revenue"
          icon={<TrendingUp className="w-4 h-4" />}
          label="Revenue driven"
          value={fmt(s.revenue)}
          accent={s.shipping_revenue > 0 ? `incl. ${fmt(s.shipping_revenue)} shipping` : "customer total"}
        />
        <StatCard
          testid="stat-commission"
          icon={<DollarSign className="w-4 h-4" />}
          label="Your commission"
          value={fmt(s.commission_total)}
          accent={s.pending_total > 0
            ? `${fmt(s.earned_total || 0)} earned · ${fmt(s.pending_total)} pending`
            : "earned to date"}
          highlight
        />
      </div>

      {/* Payout box — unpaid balance + Request payout button */}
      <PayoutBox stats={s} pendingRequest={data.pending_payout_request} onRequested={load} />

      {/* Top products + recent conversions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display font-black text-xl mb-3">Top products you've sold</h2>
          {data.top_products.length === 0 ? (
            <div className="border border-neutral-200 p-4 text-sm text-neutral-500">
              No conversions yet. Share your link on <a href="https://wa.me/" target="_blank" rel="noopener noreferrer" className="underline">WhatsApp</a> or <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" className="underline">Facebook</a> to get started.
            </div>
          ) : (
            <ul className="border border-neutral-200">
              {data.top_products.map((p) => (
                <li key={p._id} className="px-3 py-2.5 border-b border-neutral-200 last:border-b-0 flex items-center justify-between gap-2 min-w-0">
                  <Link to={`/products/${p._id}`} className="text-sm font-medium hover:underline truncate flex-1 min-w-0">
                    {p.title || p._id}
                  </Link>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] text-neutral-500">{p.qty} sold</div>
                    <div className="text-xs font-mono">{fmt(p.revenue)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h2 className="font-display font-black text-xl mb-3">Recent conversions</h2>
          {data.recent_conversions.length === 0 ? (
            <div className="border border-neutral-200 p-4 text-sm text-neutral-500">
              Nothing yet — share your link to start earning.
            </div>
          ) : (
            <ul className="border border-neutral-200">
              {data.recent_conversions.map((o) => {
                const state = o.commission_state || "pending";
                const earned = state === "earned";
                const voided = state === "voided";
                const colorText = earned ? "text-[#00A355]" : voided ? "text-neutral-400 line-through" : "text-[#FF9500]";
                const stateBg =
                  earned ? "bg-[#00A355] text-white"
                  : voided ? "bg-neutral-200 text-neutral-600"
                  : "bg-[#FFE600] text-black";
                return (
                  <li key={o.order_id} className="px-3 py-2.5 border-b border-neutral-200 last:border-b-0 min-w-0">
                    {/* Top row: order id (left) + amount (right) */}
                    <div className="flex items-start justify-between gap-2 min-w-0">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[11px] truncate">{o.order_id}</div>
                        <div className="text-[10px] text-neutral-500">
                          {new Date(o.created_at).toLocaleDateString()} · {o.status?.toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold">{fmt(o.total)}</div>
                      </div>
                    </div>

                    {/* Commission row: state badge + earned amt + % */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span
                        data-testid={`comm-state-${o.order_id}`}
                        className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${stateBg}`}
                      >
                        {state}
                      </span>
                      <span
                        data-testid={`comm-amount-${o.order_id}`}
                        className={`text-[11px] font-bold ${colorText}`}
                      >
                        {earned ? "+" : voided ? "−" : ""}{fmt(o.commission || 0)} {voided ? "voided" : "earned"}
                      </span>
                      {o.commission > 0 && o.subtotal > 0 && (
                        <span
                          data-testid={`commission-rate-${o.order_id}`}
                          className="text-[11px] font-bold text-[#FF2A00] uppercase tracking-wide"
                          title="Blended commission rate on item subtotal"
                        >
                          {(o.commission / o.subtotal * 100).toFixed(o.commission / o.subtotal * 100 < 10 ? 1 : 0)}% commissions
                        </span>
                      )}
                    </div>

                    {/* Breakdown row: items / shipping / discount — each on its own line on mobile */}
                    {(o.shipping > 0 || o.subtotal > 0) && (
                      <div className="mt-1.5 grid grid-cols-1 sm:flex sm:flex-wrap sm:gap-x-3 gap-y-0.5 text-[10px] text-neutral-500">
                        <span>Items: <b className="text-black">{fmt(o.subtotal || 0)}</b></span>
                        {o.shipping > 0 && (
                          <span>
                            Shipping: <b className="text-black">{fmt(o.shipping)}</b>{" "}
                            <span className="text-neutral-400">(no commission)</span>
                          </span>
                        )}
                        {o.discount_amount > 0 && (
                          <span>Discount: <b className="text-[#FF2A00]">−{fmt(o.discount_amount)}</b></span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, accent, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`p-3 sm:p-4 border min-w-0 overflow-hidden ${highlight ? "border-2 border-[#00A355] bg-[#F0FFF4]" : "border-neutral-200 bg-white"}`}
    >
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold ${highlight ? "text-[#00A355]" : "text-neutral-500"} mb-2 break-words`}>
        {icon}
        <span className="break-words">{label}</span>
      </div>
      <div className="text-xl sm:text-3xl font-display font-black leading-tight break-words">{value}</div>
      {accent && <div className="text-[11px] text-neutral-500 mt-1 break-words leading-snug">{accent}</div>}
    </div>
  );
}

function PayoutBox({ stats, pendingRequest, onRequested }) {
  const [busy, setBusy] = useState(false);
  const unpaid = stats.unpaid_amount || 0;
  const pending = stats.pending_total || 0;
  const requestPayout = async () => {
    if (busy) return;
    if (!window.confirm(`Request payout of ${fmt(unpaid)}?\n\nAdmin will review and pay out via your preferred method. You'll be notified when paid.`)) return;
    setBusy(true);
    try {
      await api.post("/affiliate/me/payout-request");
      toast.success("Payout requested! Admin will process it shortly.");
      onRequested?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't request payout");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 mb-8 border-2 border-[#00A355] bg-[#F0FFF4] p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#00A355] mb-1">Unpaid commission · ready to request</div>
          <div data-testid="affiliate-unpaid-amount" className="text-3xl sm:text-4xl font-display font-black break-words">{fmt(unpaid)}</div>
          <div className="text-[11px] text-neutral-600 mt-1">
            {stats.last_payout_at
              ? `Since ${new Date(stats.last_payout_at).toLocaleDateString()}`
              : "All-time earnings (no payouts yet)"}
            {" · "}Paid out on the last day of every month.
          </div>
          {pending > 0 && (
            <div
              data-testid="affiliate-pending-amount"
              className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] bg-white border border-[#FF9500] px-2 py-1.5"
            >
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF9500]">Pending</span>
              <span className="font-bold">{fmt(pending)}</span>
              <span className="text-neutral-500 text-[11px] basis-full sm:basis-auto">
                locks in when seller marks orders <b>delivered</b>
              </span>
            </div>
          )}
          <div className="text-[11px] text-neutral-500 mt-2 italic leading-snug">
            Commission only counts toward payout once the order is delivered. Cancelled orders void the commission. Shipping is never commissionable.
          </div>
        </div>
        {pendingRequest ? (
          <div className="text-right shrink-0">
            <div className="inline-block px-3 py-1.5 bg-[#FFE600] text-black text-xs font-bold uppercase tracking-widest">
              Pending review
            </div>
            <div className="text-[11px] text-neutral-600 mt-1">
              Requested {new Date(pendingRequest.requested_at).toLocaleDateString()}
            </div>
          </div>
        ) : (
          <button
            data-testid="affiliate-request-payout"
            type="button"
            onClick={requestPayout}
            disabled={busy || unpaid <= 0}
            className="shrink-0 h-10 px-4 bg-[#00A355] hover:bg-[#008844] text-white text-sm font-bold uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Requesting…" : "Request payout"}
          </button>
        )}
      </div>
    </div>
  );
}
